import React, { useMemo, useState } from "react";
import { Box, Typography, Chip, Button, Collapse, Divider } from "@mui/material";

function nPrice(x) {
  const n = Number(x);
  if (!Number.isFinite(n) || n <= 0) return "—";
  return n >= 1000 ? n.toFixed(2) : n.toFixed(6);
}

function chipSx() {
  return {
    height: 22,
    borderRadius: 1,
    borderColor: "rgba(255,255,255,.14)",
    color: "#cbd5e1",
    "& .MuiChip-label": { px: 0.7, fontWeight: 800, fontSize: 10.5 },
  };
}

function normalizeResultV2(result) {
  const r = result || {};
  const kind = r.kind || null;

  // NEW contract
  const ui = r.ui || null;
  const internal = r.internal || null;

  // Backward compatible fallback (legacy engines)
  const legacyDecision = r.decision || r.signal || r.output?.signal || null;
  const legacyPlan = r.plan || r.tradePlan || r.output?.plan || null;
  const legacyLite = r.modelLite || r.model || r.lite || null;
  const legacyGlobalCtx = r.globalCtx || null;

  return {
    kind,
    ui,
    internal,
    legacy: legacyDecision || legacyPlan || legacyLite || legacyGlobalCtx
      ? { decision: legacyDecision, plan: legacyPlan, lite: legacyLite, globalCtx: legacyGlobalCtx }
      : null,
    chart: r.chart || null,
    forecast: r.forecast || null,
  };
}

export default function SignalTerminalReport({
  type,
  symbol,
  tf,
  eventId,
  status,
  error,
  result,
  messages,
}) {
  const [debugOpen, setDebugOpen] = useState(false);

  const x = useMemo(() => normalizeResultV2(result), [result]);

  // ====== PRIMARY UI PATH (NEW) ======
  const ui = x.ui;

  const headerTitle = ui?.header?.title || `${symbol}/${tf}`;
  const statusText = ui?.header?.statusText || "—";
  const oneLine = ui?.header?.oneLine || "";
  const actionText = ui?.action?.text || "";
  const actionWhy = Array.isArray(ui?.action?.why) ? ui.action.why : [];

  const sections = Array.isArray(ui?.sections) ? ui.sections : [];

  const trade = sections.find(s => s?.id === "plan")?.trade || null;

  // ====== FALLBACK PATH (LEGACY) ======
  const legacy = x.legacy;

  return (
    <Box sx={{ display: "grid", gap: 1 }}>
      {/* meta */}
      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
        {eventId ? (
          <Chip
            size="small"
            label={`eventId: ${eventId}`}
            variant="outlined"
            sx={{
              height: 22,
              borderRadius: 1,
              borderColor: "rgba(255,255,255,.14)",
              color: "#cbd5e1",
              "& .MuiChip-label": { px: 0.7, fontWeight: 800, fontSize: 10.5 },
            }}
          />
        ) : null}

        <Chip
          size="small"
          label={`status: ${String(status || "idle")}`}
          variant="outlined"
          sx={{
            height: 22,
            borderRadius: 1,
            borderColor: "rgba(255,255,255,.14)",
            color: "#cbd5e1",
            "& .MuiChip-label": { px: 0.7, fontWeight: 800, fontSize: 10.5 },
          }}
        />

        {error ? (
          <Chip
            size="small"
            label={`error: ${String(error.message || error)}`}
            variant="outlined"
            sx={{
              height: 22,
              borderRadius: 1,
              borderColor: "rgba(239,68,68,.35)",
              bgcolor: "rgba(239,68,68,.14)",
              color: "#fecaca",
              "& .MuiChip-label": { px: 0.7, fontWeight: 800, fontSize: 10.5 },
            }}
          />
        ) : null}
      </Box>

      {/* main readable card */}
      <Box
        sx={{
          p: 1.25,
          borderRadius: 2,
          border: "1px solid rgba(255,255,255,.10)",
          bgcolor: "rgba(255,255,255,.03)",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 1 }}>
          <Typography sx={{ fontWeight: 900, color: "#e5e7eb", fontSize: 13 }}>
            {type === "market_forecast" ? "Market forecast" : "Signal"}
            <span style={{ color: "rgba(229,231,235,.6)", fontWeight: 700 }}> — {headerTitle}</span>
          </Typography>

          <Chip
            size="small"
            label={String(statusText || "—").toUpperCase()}
            sx={{
              height: 20,
              borderRadius: 1,
              bgcolor: "rgba(148,163,184,.14)",
              border: "1px solid rgba(148,163,184,.28)",
              color: "#cbd5e1",
              "& .MuiChip-label": { fontWeight: 900, fontSize: 11, lineHeight: "20px", px: 0.75 },
            }}
            variant="outlined"
          />
        </Box>

        {/* NEW UI render */}
        {ui ? (
          <>
            {oneLine ? (
              <Typography sx={{ mt: 0.6, fontSize: 12.5, color: "#cbd5e1" }}>
                {oneLine}
              </Typography>
            ) : null}

            {actionText ? (
              <Typography sx={{ mt: 0.6, fontSize: 12.5, color: "#e5e7eb", fontWeight: 800 }}>
                {actionText}
              </Typography>
            ) : null}

            {actionWhy.length ? (
              <Typography sx={{ mt: 0.5, fontSize: 12, color: "rgba(229,231,235,.85)" }}>
                {actionWhy.join(" · ")}
              </Typography>
            ) : null}

            <Divider sx={{ my: 1, borderColor: "rgba(255,255,255,.08)" }} />

            {/* sections */}
            <Box sx={{ display: "grid", gap: 1 }}>
              {sections.map((sec) => (
                <Box key={sec.id} sx={{ display: "grid", gap: 0.5 }}>
                  <Typography sx={{ fontSize: 12, color: "#94a3b8", fontWeight: 800 }}>
                    {sec.title}
                  </Typography>

                  <Box sx={{ display: "grid", gap: 0.25 }}>
                    {(sec.bullets || []).map((b, i) => (
                      <Typography key={i} sx={{ fontSize: 12, color: "rgba(229,231,235,.9)" }}>
                        • {b}
                      </Typography>
                    ))}
                  </Box>

                  {/* plan chips if trade exists */}
                  {sec.id === "plan" ? (
                    <>
                      <Divider sx={{ my: 0.8, borderColor: "rgba(255,255,255,.08)" }} />
                      <Typography sx={{ fontSize: 12, color: "#94a3b8", fontWeight: 800 }}>
                        Trade levels
                      </Typography>

                      {!trade ? (
                        <Typography sx={{ mt: 0.4, fontSize: 12, color: "#cbd5e1" }}>
                          No confirmed entry — plan is hidden.
                        </Typography>
                      ) : (
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.8, mt: 0.6 }}>
                          <Chip size="small" label={`Entry: ${nPrice(trade.entry)}`} variant="outlined" sx={chipSx()} />
                          <Chip size="small" label={`Stop: ${nPrice(trade.sl)}`} variant="outlined" sx={chipSx()} />
                          <Chip
                            size="small"
                            label={`TPs: ${(trade.tps || []).map(nPrice).join(" / ") || "—"}`}
                            variant="outlined"
                            sx={chipSx()}
                          />
                        </Box>
                      )}
                    </>
                  ) : null}
                </Box>
              ))}
            </Box>
          </>
        ) : legacy ? (
          // Legacy fallback: keep minimal instead of breaking UI
          <Typography sx={{ mt: 0.6, fontSize: 12.5, color: "#cbd5e1" }}>
            Legacy result received (no ui). Update backend to return result.ui.
          </Typography>
        ) : (
          <Typography sx={{ mt: 0.6, fontSize: 12.5, color: "#cbd5e1" }}>
            No result yet.
          </Typography>
        )}

        {/* Debug */}
        <Box sx={{ mt: 1 }}>
          <Button
            onClick={() => setDebugOpen((v) => !v)}
            variant="text"
            sx={{ textTransform: "none", fontWeight: 900, color: "rgba(229,231,235,.85)" }}
          >
            {debugOpen ? "Hide debug" : "Show debug"}
          </Button>

          <Collapse in={debugOpen} unmountOnExit>
            <Box
              sx={{
                mt: 0.8,
                borderRadius: 2,
                border: "1px solid rgba(255,255,255,.10)",
                bgcolor: "rgba(2,6,23,.35)",
                p: 1,
                maxHeight: 260,
                overflowY: "auto",
              }}
            >
              <Typography sx={{ fontWeight: 900, fontSize: 12.5, mb: 0.75 }}>
                Stream messages
              </Typography>

              {!messages?.length ? (
                <Typography sx={{ color: "rgba(229,231,235,.65)", fontSize: 12 }}>
                  No messages yet.
                </Typography>
              ) : (
                <Box sx={{ display: "grid", gap: 0.75 }}>
                  {messages.map((m) => (
                    <Box
                      key={m.id}
                      sx={{
                        p: 0.9,
                        borderRadius: 1.5,
                        border: "1px solid rgba(255,255,255,.08)",
                        bgcolor: "rgba(2,6,23,.35)",
                      }}
                    >
                      {m.title ? (
                        <Typography sx={{ fontWeight: 900, fontSize: 12, mb: 0.25 }}>
                          {m.title}
                        </Typography>
                      ) : null}
                      <Typography sx={{ whiteSpace: "pre-wrap", fontSize: 12, color: "#e5e7eb" }}>
                        {m.content || ""}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          </Collapse>
        </Box>
      </Box>
    </Box>
  );
}
